import type { ReactNode } from 'react';
import type { MeResponse, SchoolSummary } from '@ucp/web/lib';
import { cn } from '../ui/cn';
import { useSchoolContext } from '../SchoolContextProvider';
import { useSchoolDirectory } from '../SchoolDirectoryProvider';
import { SchoolSwitcher } from '../SchoolSwitcher';
import { assignedSchoolIds } from '../assignedSchoolIds';
import { ShellWorkLink } from '../my-work/ShellWorkLink';
import { AccountMenu } from './AccountMenu';
import { PortalWordmark } from './PortalWordmark';
import { SHELL_HEADER_GRID_CLASS } from './shellLayout';

/**
 * Application banner (PRO-0250, D3; reskinned PRO-0260 Phase 1; header nav refinement
 * ADR-014 Amendment 7). Hosts (mobile) the tier-1 module burger, the wordmark, the
 * ambient `SchoolSwitcher` (the single, always-visible active-school control) and the
 * account affordance — now a person-icon `AccountMenu` drawer consolidating identity +
 * sign-out. The switcher hides itself for single-school and L6 users (≤1 option) and is
 * reused verbatim (PRO-0030).
 *
 * Mobile layout is a **single compact toolbar row** (burger icon only, truncated wordmark,
 * compact school select, account icon) — no stacked header bands or visible module label
 * on the burger (PRO-0540 refinement). Desktop keeps the two-column grid above rail |
 * workspace.
 *
 * `moduleNav` is the mobile tier-1 burger (`ModuleNav`, `md:hidden`), injected by the
 * shell so the banner stays decoupled from the module registry. School names come from
 * `SchoolDirectoryProvider` (`GET /schools`); UUIDs are a loading/fallback label only.
 */
export function AppHeader({
  user,
  onSignOut,
  moduleNav,
}: {
  user: MeResponse;
  onSignOut: () => void;
  moduleNav?: ReactNode;
}) {
  const { activeSchoolId, setActiveSchoolId } = useSchoolContext();
  const { schools: directorySchools } = useSchoolDirectory();
  const assigned = assignedSchoolIds(user);
  const nameById = new Map(directorySchools.map((school) => [school.schoolId, school.name]));

  const options: SchoolSummary[] = assigned.map((schoolId) => ({
    schoolId,
    name: nameById.get(schoolId) ?? schoolId,
  }));

  const schoolSwitcher =
    assigned.length > 1 ? (
      <SchoolSwitcher
        schools={options}
        activeSchoolId={activeSchoolId ?? ''}
        onChange={setActiveSchoolId}
        compact
      />
    ) : null;

  return (
    <header role="banner" className={SHELL_HEADER_GRID_CLASS}>
      <div className="flex items-center gap-2 py-2 md:contents">
        <div className="flex min-w-0 flex-1 items-center gap-2 md:flex-none md:py-3 md:pr-3">
          {moduleNav}
          <PortalWordmark compact className="min-w-0 flex-1 md:flex-initial" />
        </div>
        <div
          className={cn(
            'flex shrink-0 items-center gap-1.5',
            'md:w-full md:justify-between md:gap-3 md:px-4 md:py-3',
          )}
        >
          {schoolSwitcher}
          <div className="flex items-center gap-1.5 md:ml-auto md:gap-3">
            <ShellWorkLink user={user} variant="header" />
            <AccountMenu user={user} onSignOut={onSignOut} />
          </div>
        </div>
      </div>
    </header>
  );
}
